import { useState, useEffect } from 'react'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { DatePicker, Tabs, InputContainer, SwitchToggle } from 'components/ideal-core-ui'
import s from './LogTimeModal.scss'
import '!style-loader!css-loader!react-datepicker/dist/react-datepicker.css' // eslint-disable-line
import { timezoneDate, utcDate } from 'utils/helpers'
import { toCompaniesSelect } from 'components/Companies/serializers'
import { isTimetrackerAdmin, getUser, getTimezone } from 'utils/authService'
import Select from 'react-select'
import { toTypesOfWork } from './serializers'
import { makeGetRequest, makePostRequest, makePutRequest } from 'utils/api'
import { useImmer } from 'use-immer'
import isEqual from 'lodash/isEqual'
import { useDispatch } from 'react-redux'
import { IoWarning } from 'react-icons/io5'

const loggedInUser = getUser()

const LogTimeModal = ({
  toggleLogTimeModal,
  showLogTimeModal,
  users,
  editedEntryId,
  getLogs,
  dealId,
  onDelete
}) => {
  const [selectedCompanies, setSelectedCompanies] = useState(null)
  const [typesOfWork, setTypesOfWork] = useState([])
  const [typesOfTimeoff, setTypesOfTimeoff] = useState([])
  const [selectedUser, setSelectedUser] = useState({
    value: loggedInUser.id,
    label: loggedInUser.name,
    timezone: getTimezone()
  })
  const [hours, setHours] = useState(null)
  const [timeOffHours, setTimeOffHours] = useState(null)
  const [date, setDate] = useState(undefined)
  const [timeOffDate, setTimeOffDate] = useState(undefined)
  const [typeOfWork, setTypeOfWork] = useState(null)
  const [isBillable, setIsBillable] = useState(true)
  const [isOvertime, setIsOvertime] = useState(false)
  const [isSecondShift, setIsSecondShift] = useState(false)
  const [isThirdShift, setIsThirdShift] = useState(false)
  const [notes, setNotes] = useState('')
  const [timeOffNotes, setTimeOffNotes] = useState('')
  const [companies, setCompanies] = useState([])
  const [clientsHours, setClientsHours] = useImmer([])
  const [prevClientsHours, setPrevClientsHours] = useImmer([])
  const [selectedTab, setSelectedTab] = useState('workTime')
  const [timeOffType, setTimeOffType] = useState(null)
  const dispatch = useDispatch()

  if (!isEqual(prevClientsHours, clientsHours)) {
    setPrevClientsHours(clientsHours)
    setHours(
      clientsHours.reduce((acc, client) => {
        if (client.hours === '') return ''
        return acc + +client.hours
      }, 0)
    )
  }

  useEffect(() => {
    ;(async () => {
      const response = await makeGetRequest('/v1/timetracker/type-of-work')
      setTypesOfWork(toTypesOfWork(response))

      const types = await makeGetRequest('/v1/timetracker/time-off-type')
      setTypesOfTimeoff(types.filter(t => !t.system))
      if (!editedEntryId) setTimeOffType(types[0])

      const companies = await makeGetRequest(`/v1/deals/${dealId}/companies`)
      setCompanies(companies.map(({ company }) => toCompaniesSelect([company])[0]))

      if (companies.length === 1) {
        setSelectedCompanies([toCompaniesSelect([companies[0].company])[0]])
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ;(async () => {
      if (editedEntryId) {
        try {
          const response = await makeGetRequest(`/v1/timetracker/${editedEntryId}`)

          setSelectedUser({ value: response.user.id, label: response.user.fullName })

          if (!response.deal) {
            setSelectedTab('timeOff')
            setTimeOffDate(response.workDate)
            setTimeOffHours(response.hours)
            setTimeOffNotes(response.notes)
            setTimeOffType(response.timeOffType)
            return
          }

          setNotes(response.notes)
          setDate(response.workDate)
          setHours(response.hours)

          setSelectedCompanies(
            response.companySplit.map(({ company }) => toCompaniesSelect([company])[0])
          )
          if (response.companySplit?.length) {
            setClientsHours(() => {
              return response.companySplit.map(
                ({ company, hours, typeOfWork, billable, workTimeType }) => ({
                  company: toCompaniesSelect([company])[0],
                  hours,
                  typeOfWork: toTypesOfWork([typeOfWork])[0],
                  isBillable: billable,
                  isOvertime: workTimeType === 'OVERTIME',
                  isSecondShift: workTimeType === 'SECOND_SHIFT',
                  isThirdShift: workTimeType === 'THIRD_SHIFT'
                })
              )
            })
          } else {
            setTypeOfWork(toTypesOfWork([response.typeOfWork])[0])
            setIsBillable(response.billable)
            setIsOvertime(response.workTimeType === 'OVERTIME')
            setIsSecondShift(response.workTimeType === 'SECOND_SHIFT')
            setIsThirdShift(response.workTimeType === 'THIRD_SHIFT')
          }
        } catch (e) {
          dispatch({
            type: 'PUSH_NOTIFICATION',
            payload: {
              title: 'There was a problem saving Time Log',
              message: e?.response?.data || e.message,
              level: 'error'
            }
          })
        }
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedEntryId])

  const getCompanyHours = () => {
    if (!selectedCompanies || selectedCompanies.length === 0) {
      return []
    }
    return clientsHours.map(client => ({
      companyId: client.company.value,
      hours: client.hours,
      typeOfWorkId: client.typeOfWork.value,
      billable: client.isBillable ?? true,
      workTimeType: client.isOvertime
        ? 'OVERTIME'
        : client.isSecondShift
          ? 'SECOND_SHIFT'
          : client.isThirdShift
            ? 'THIRD_SHIFT'
            : 'REGULAR'
    }))
  }

  const saveLog = async () => {
    let log = {}
    if (selectedTab === 'workTime') {
      log = {
        userId: selectedUser.value,
        workDate: date,
        hours,
        typeOfWorkId: typeOfWork?.value,
        dealId,
        companySplit: getCompanyHours(),
        billable: isBillable,
        workTimeType: isOvertime
          ? 'OVERTIME'
          : isSecondShift
            ? 'SECOND_SHIFT'
            : isThirdShift
              ? 'THIRD_SHIFT'
              : 'REGULAR',
        notes
      }
    } else {
      log = {
        userId: selectedUser.value,
        workDate: timeOffDate,
        hours: timeOffHours,
        timeOffTypeId: timeOffType.id,
        notes: timeOffNotes
      }
    }
    try {
      if (editedEntryId) {
        await makePutRequest(`/v1/timetracker/${editedEntryId}`, log)
      } else {
        await makePostRequest('/v1/timetracker', log)
      }
      getLogs()
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'Time Log was saved successfully',
          level: 'success'
        }
      })

      toggleLogTimeModal()
    } catch (error) {
      toggleLogTimeModal()
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'There was a problem saving Time Log',
          level: 'error'
        }
      })
    }
  }

  const isDisabled = () => {
    if (selectedTab === 'workTime') {
      if (selectedCompanies && selectedCompanies.length) {
        return (
          !date ||
          !clientsHours.length ||
          clientsHours.some(company => !company.hours || !company.typeOfWork)
        )
      }
      return !hours || !date || !typeOfWork
    } else {
      return !timeOffHours || !timeOffDate
    }
  }

  return (
    <>
      <Modal
        keyboard={false}
        backdrop="static"
        isOpen={showLogTimeModal}
        toggle={toggleLogTimeModal}
        size="lg"
      >
        <ModalHeader toggle={toggleLogTimeModal} className={s.modalHeader}>
          {editedEntryId ? 'Edit' : ''} Log Time
        </ModalHeader>
        <ModalBody>
          <Tabs
            className={s.tabContainer}
            noPadding
            activeTabKey={selectedTab}
            tabs={[
              {
                key: 'workTime',
                cb: () => setSelectedTab('workTime'),
                label: 'Log Work Time',
                value: (
                  <div className={s.workTimeWrapper}>
                    {isTimetrackerAdmin() && (
                      <div style={{ display: 'flex' }}>
                        <div>
                          <label className={s.label}>User</label>
                          <Select
                            onChange={value => {
                              setSelectedUser(value)
                            }}
                            options={users}
                            className={s.userSelect}
                            value={selectedUser}
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative',
                            top: 15,
                            marginLeft: 20,
                            fontSize: 13,
                            lineHeight: '14px'
                          }}
                        >
                          <IoWarning
                            style={{
                              color: '#ffd000',
                              fontSize: 40,
                              marginRight: 10,
                              position: 'relative',
                              bottom: 8
                            }}
                          />
                          <p>
                            All dates will be saved in the timezone of the selected user [
                            {selectedUser.timezone}].
                          </p>
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', marginBottom: '20px' }}>
                      <div style={{ marginRight: '20px' }}>
                        <label className={s.label}>*Hours</label>
                        <InputContainer
                          type="number"
                          placeholder="#"
                          classname={s.hours}
                          cb={value => setHours(value)}
                          text={hours || ''}
                          isDisabled={selectedCompanies && selectedCompanies.length}
                          min={0}
                        />
                      </div>
                      <div>
                        <label className={s.label}>*Date</label>
                        <DatePicker
                          value={
                            date
                              ? timezoneDate(date, null, null, null, null, selectedUser.timezone)
                              : ''
                          }
                          className={s.datePicker}
                          onChange={value => {
                            const date = value ? utcDate(value, null, selectedUser.timezone) : ''
                            setDate(date)
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', marginBottom: '20px' }}>
                      <div className={s.company}>
                        <label className={s.label}>Select Companies</label>
                        <Select
                          onChange={values => {
                            setSelectedCompanies(values)
                          }}
                          options={companies}
                          className={s.userSelect}
                          value={selectedCompanies}
                          isMulti
                        />
                      </div>
                    </div>
                    {selectedCompanies && selectedCompanies.length ? (
                      <div className={s.list}>
                        {selectedCompanies.map((company, i) => (
                          <div style={{ display: 'flex' }} key={company.value}>
                            <p className={s.companyName}>{company.label}</p>
                            <div>
                              <label className={s.label}>*Hours</label>
                              <InputContainer
                                type="number"
                                placeholder="#"
                                classname={s.hours}
                                cb={value => {
                                  setClientsHours(draft => {
                                    if (draft[i]) {
                                      draft[i].hours = value
                                    } else {
                                      draft[i] = { hours: value, company }
                                    }
                                  })
                                }}
                                text={
                                  selectedCompanies.length === 1
                                    ? hours
                                    : clientsHours[i]?.hours || ''
                                }
                                min={0}
                              />
                            </div>
                            <div style={{ display: 'flex', marginBottom: '20px' }}>
                              <div className={s.company}>
                                <label className={s.label}>*Type of Work</label>
                                <Select
                                  onChange={value => {
                                    setClientsHours(draft => {
                                      if (draft[i]) {
                                        draft[i].typeOfWork = value
                                      } else {
                                        draft[i] = {
                                          typeOfWork: value,
                                          company,
                                          ...(selectedCompanies.length === 1 ? { hours } : {})
                                        }
                                      }
                                    })
                                  }}
                                  options={typesOfWork}
                                  className={s.clientTypeOfWork}
                                  value={clientsHours[i]?.typeOfWork || ''}
                                />
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  top: 8,
                                  position: 'relative'
                                }}
                              >
                                <div>
                                  <SwitchToggle
                                    defaultOn={clientsHours[i]?.isBillable ?? true}
                                    className={s.marginRight}
                                    noText
                                    id={`billable-${i}`}
                                    onToggle={value => {
                                      setClientsHours(draft => {
                                        if (draft[i]) {
                                          draft[i].isBillable = value
                                        } else {
                                          draft[i] = { isBillable: value, company }
                                        }
                                      })
                                    }}
                                  >
                                    Billable
                                  </SwitchToggle>
                                </div>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  <SwitchToggle
                                    defaultOn={clientsHours[i]?.isOvertime || false}
                                    className={s.marginRight}
                                    noText
                                    id={`overtime-${i}`}
                                    onToggle={value => {
                                      setClientsHours(draft => {
                                        if (draft[i]) {
                                          draft[i].isOvertime = value
                                          draft[i].isSecondShift = false
                                        } else {
                                          draft[i] = { isOvertime: value, company }
                                        }
                                      })
                                    }}
                                  >
                                    Overtime
                                  </SwitchToggle>
                                  <SwitchToggle
                                    defaultOn={clientsHours[i]?.isSecondShift || false}
                                    noText
                                    id={`second-shift-${i}`}
                                    onToggle={value => {
                                      setClientsHours(draft => {
                                        if (draft[i]) {
                                          draft[i].isSecondShift = value
                                          draft[i].isOvertime = false
                                        } else {
                                          draft[i] = { isSecondShift: value, company }
                                        }
                                      })
                                    }}
                                  >
                                    Second shift
                                  </SwitchToggle>
                                  <SwitchToggle
                                    defaultOn={clientsHours[i]?.isThirdShift || false}
                                    noText
                                    id={`third-shift-${i}`}
                                    onToggle={value => {
                                      setClientsHours(draft => {
                                        if (draft[i]) {
                                          draft[i].isThirdShift = value
                                          draft[i].isOvertime = false
                                          draft[i].isSecondShift = false
                                        } else {
                                          draft[i] = { isThirdShift: value, company }
                                        }
                                      })
                                    }}
                                  >
                                    Third shift
                                  </SwitchToggle>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', marginBottom: '20px' }}>
                        <div className={s.company}>
                          <label className={s.label}>*Select Type of Work</label>
                          <Select
                            onChange={value => {
                              setTypeOfWork(value)
                            }}
                            options={typesOfWork}
                            className={s.userSelect}
                            value={
                              clientsHours.length === 1 ? clientsHours[0].typeOfWork : typeOfWork
                            }
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            top: 8,
                            position: 'relative'
                          }}
                        >
                          <div>
                            <SwitchToggle
                              id="billable"
                              defaultOn={
                                clientsHours.length === 1 ? clientsHours[0].isBillable : isBillable
                              }
                              className={s.marginRight}
                              noText
                              onToggle={setIsBillable}
                            >
                              Billable
                            </SwitchToggle>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <SwitchToggle
                              id="over-time"
                              defaultOn={
                                clientsHours.length === 1 ? clientsHours[0].isOvertime : isOvertime
                              }
                              className={s.marginRight}
                              noText
                              onToggle={v => {
                                setIsOvertime(v)
                                setIsSecondShift(false)
                              }}
                            >
                              Overtime
                            </SwitchToggle>
                            <SwitchToggle
                              id="second-shift"
                              defaultOn={
                                clientsHours.length === 1
                                  ? clientsHours[0].isSecondShift
                                  : isSecondShift
                              }
                              noText
                              onToggle={v => {
                                setIsSecondShift(v)
                                setIsOvertime(false)
                              }}
                            >
                              Second shift
                            </SwitchToggle>
                          </div>
                        </div>
                      </div>
                    )}
                    <label className={s.label}>Description of Work activity</label>
                    <InputContainer
                      type="textarea"
                      placeholder="Description of Work activity"
                      classname={s.notes}
                      cb={value => setNotes(value)}
                      text={notes || ''}
                    />
                  </div>
                )
              },
              {
                key: 'timeOff',
                label: 'Time Off',
                cb: () => setSelectedTab('timeOff'),
                value: (
                  <div className={s.workTimeWrapper}>
                    {isTimetrackerAdmin() && (
                      <>
                        <label className={s.label}>User</label>
                        <Select
                          onChange={value => {
                            setSelectedUser(value)
                          }}
                          options={users}
                          className={s.userSelect}
                          value={selectedUser}
                        />
                      </>
                    )}
                    <div style={{ display: 'flex', marginBottom: '20px' }}>
                      <div style={{ marginRight: '20px' }}>
                        <label className={s.label}>*Hours</label>
                        <InputContainer
                          type="number"
                          placeholder="#"
                          classname={s.hours}
                          cb={value => setTimeOffHours(value)}
                          text={timeOffHours || ''}
                          min={0}
                        />
                      </div>
                      <div>
                        <label className={s.label}>*Date</label>
                        <DatePicker
                          value={
                            timeOffDate
                              ? timezoneDate(
                                  timeOffDate,
                                  null,
                                  null,
                                  null,
                                  null,
                                  selectedUser.timezone
                                )
                              : ''
                          }
                          className={s.datePicker}
                          onChange={value => {
                            const date = value ? utcDate(value, null, selectedUser.timezone) : ''
                            setTimeOffDate(date)
                          }}
                        />
                      </div>
                    </div>
                    {typesOfTimeoff.map((type, i) => (
                      <div key={type.id}>
                        <input
                          type="radio"
                          checked={timeOffType ? timeOffType.id === type.id : i === 0}
                          onChange={() => {
                            setTimeOffType(type)
                          }}
                        />{' '}
                        {type.name}
                      </div>
                    ))}
                    <label style={{ marginTop: 20 }} className={s.label}>
                      Description of Work activity
                    </label>
                    <InputContainer
                      type="textarea"
                      placeholder="Description of Work activity"
                      classname={s.notes}
                      cb={value => setTimeOffNotes(value)}
                      text={timeOffNotes || ''}
                    />
                  </div>
                )
              }
            ]}
          />
        </ModalBody>
        <ModalFooter>
          {editedEntryId && (
            <Button
              className={s.delete}
              color="danger"
              onClick={() => {
                onDelete(toggleLogTimeModal)
              }}
            >
              Delete
            </Button>
          )}
          <Button className={s.saveBtn} onClick={saveLog} disabled={isDisabled()}>
            Save
          </Button>
          <Button color="secondary" onClick={toggleLogTimeModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default LogTimeModal
